import { functionWithFG } from '@atlaskit/platform-feature-flags-react';
import type { Extension } from '@atlassian/forge-ui/ui';
import type ApolloClient from 'apollo-client';
import { useEffect, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';
import type RelayModernEnvironment from 'relay-runtime/lib/store/RelayModernEnvironment';
import {
	getCachedTypedChildrenContent,
	queryTypedChildrenContent,
} from '../../utils/queryTypedChildrenContent';

type Props = {
	client: ApolloClient<object>;
	contentContainerId: string;
	contentKey: string;
	extension: Extension;
};

const useConditionalRelayEnvironment = functionWithFG<() => RelayModernEnvironment | undefined>(
	'forge-ui-add-relay-get-custom-content-query',
	useRelayEnvironment,
	() => undefined,
);

const getCustomContentType = (extension: Extension) => {
	const appId = extension?.appId,
		envId = extension?.environmentId,
		customContentKey = extension?.properties?.staticView?.customContentTypeKey;

	if (!appId || !envId || !customContentKey) {
		return null;
	}

	return `forge:${appId}:${envId}:${customContentKey}`;
};

const useMacroStaticContentViewImpl = <T>({
	client,
	extension,
	contentContainerId,
	contentKey,
}: Props): { content: T | null; isLoading: boolean } => {
	const childContentType = getCustomContentType(extension);
	const hasStaticViewDefinition = !!extension?.properties?.staticView && !!childContentType;
	const cachedContentValue = hasStaticViewDefinition
		? getCachedTypedChildrenContent({ contentContainerId, childContentType })?.[contentKey]
		: null;
	const relayEnvironment = useConditionalRelayEnvironment();

	const [{ isLoading, content }, setState] = useState<{
		content: unknown | null;
		isLoading: boolean;
	}>({
		isLoading: hasStaticViewDefinition && !cachedContentValue,
		content: cachedContentValue,
	});

	useEffect(() => {
		// First render matches content.
		if (!!cachedContentValue && cachedContentValue === content) {
			return;
		}

		// No content -> content found (e.g. contentKey changed).
		if (!!cachedContentValue && !content) {
			setState({
				isLoading: false,
				content: cachedContentValue,
			});

			return;
		}

		let cancelled = false;

		// Content type not yet queried.
		(async () => {
			if (!childContentType) {
				return;
			}

			const childrenContent = await queryTypedChildrenContent({
				apolloClient: client,
				contentContainerId,
				childContentType,
				relayEnvironment,
			}).catch(() => null);

			if (cancelled || !childrenContent) {
				return;
			}

			setState({
				isLoading: false,
				content: childrenContent[contentKey],
			});
		})();

		return () => {
			cancelled = true;
		};
	}, [
		content,
		cachedContentValue,
		contentKey,
		childContentType,
		client,
		contentContainerId,
		relayEnvironment,
	]);

	return {
		isLoading,
		content: content as T | null,
	};
};

const defaultImpl = <T>(_: Props) => ({
	isLoading: false,
	content: null as T | null,
});

export const useMacroStaticContentView = functionWithFG(
	'forge-ui-static-macros',
	useMacroStaticContentViewImpl,
	defaultImpl,
);
